/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { tokens } from '@neo4j-ndl/base';
import type React from 'react';

import {
  type PolymorphicCommonProps,
  type Radius,
  type ReactComponent,
  type Spacing,
} from '../_common/types';
import { getRadius, getSpacing } from '../_common/utils';

type AllowedFlexElements = 'div' | 'span' | 'ul' | 'ol' | 'li';

type BaseFlexProps = {
  /**
   * @default tokens.space['4']
   */
  gap?: Spacing;
  /**
   * @default undefined
   */
  rowGap?: Spacing;
  /**
   * @default undefined
   */
  columnGap?: Spacing;
  /**
   * @default undefined
   */
  borderRadius?: Radius;
  /**
   * Flex direction is set to column by default.
   * The `row-reverse` and `column-reverse` values
   * are skipped due to accessibility concerns.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction#accessibility_concerns
   *
   * @default row
   */
  flexDirection?: Exclude<
    React.CSSProperties['flexDirection'],
    'row-reverse' | 'column-reverse'
  >;
  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap
   * @default nowrap
   */
  flexWrap?: React.CSSProperties['flexWrap'];
  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
   * @default undefined
   */
  justifyContent?: React.CSSProperties['justifyContent'];
  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/align-items
   * @default undefined
   */
  alignItems?: React.CSSProperties['alignItems'];
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

/**
 * Flex is a general-purpose container that helps
 * lay out components with consistent spacing in a row or column.
 */
export const Flex = <T extends AllowedFlexElements = 'div'>({
  as,
  gap = '8',
  rowGap,
  columnGap,
  children,
  borderRadius,
  flexDirection = 'column',
  flexWrap = 'nowrap',
  alignItems,
  justifyContent,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, BaseFlexProps>) => {
  const Component = (as || 'div') as unknown as ReactComponent;

  // If gap is not provided,
  // and rowGap and columnGap are not provided,
  // then use the default value.
  const gapPx = getSpacing(gap, 'gap');
  const rowGapPx = rowGap ? tokens.space[rowGap] : undefined;
  const columnGapPx = columnGap ? tokens.space[columnGap] : undefined;
  const gapFormatted = `${rowGapPx ?? gapPx} ${columnGapPx ?? gapPx}`;

  const additionalStyle: React.CSSProperties = {
    alignItems,
    borderRadius: getRadius(borderRadius, 'borderRadius'),
    display: 'flex',
    flexDirection,
    flexWrap,
    gap: gapFormatted,
    justifyContent,
  };

  return (
    <Component
      ref={ref}
      className={className}
      style={{ ...additionalStyle, ...style }}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};
